import React from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, Package, ShoppingBag } from "lucide-react";
import { Layout } from "../components/Layout";
import { SponsorSlot } from "../components/SponsorSlot";
import { useI18n } from "../i18n";
import { eur } from "../lib/format";
import * as apiClient from "../api";

const DESTINATIONS = [
  { slug: "", label: "Tutte le destinazioni" },
  { slug: "gargano", label: "Gargano" },
  { slug: "dolomiti", label: "Dolomiti" },
];

export default function Shop() {
  const { t } = useI18n();
  const [params, setParams] = useSearchParams();
  const destination = params.get("destination") || "";
  const category = params.get("category") || "";
  const q = params.get("q") || "";

  const { data: categories = [] } = useQuery({ queryKey: ["productCategories"], queryFn: apiClient.getProductCategories });
  const { data: products = [], isLoading } = useQuery({
    queryKey: ["products", destination, category, q],
    queryFn: () => apiClient.getProducts({ destination: destination || undefined, category: category || undefined, q: q || undefined }),
  });

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  return (
    <Layout noPadding>
      {/* header */}
      <section className="bg-[#0A1128] text-white" data-testid="shop-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-secondary mb-4">Dal territorio</div>
          <h1 className="font-heading font-semibold text-4xl sm:text-6xl leading-[0.98] tracking-tight max-w-2xl">Portati a casa un pezzo del viaggio.</h1>
          <p className="mt-5 text-lg text-white/80 max-w-xl">Prodotti, artigianato e piccoli pezzi di territorio selezionati da Cristalda.</p>
          <p className="mt-4 font-accent italic text-secondary text-xl">Il problema del viaggio? Prima o poi devi tornare.</p>
        </div>
      </section>

      {/* filters */}
      <div className="sticky top-16 z-20 bg-white/90 backdrop-blur border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center gap-3">
          <select data-testid="shop-filter-destination" value={destination} onChange={(e) => setParam("destination", e.target.value)}
            className="bg-white border border-slate-200 rounded-full px-4 py-2 text-sm font-medium outline-none focus:ring-2 focus:ring-secondary">
            {DESTINATIONS.map((d) => <option key={d.slug} value={d.slug}>{d.label}</option>)}
          </select>
          <select data-testid="shop-filter-category" value={category} onChange={(e) => setParam("category", e.target.value)}
            className="bg-white border border-slate-200 rounded-full px-4 py-2 text-sm font-medium outline-none focus:ring-2 focus:ring-secondary">
            <option value="">Tutte le categorie</option>
            {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input data-testid="shop-search" defaultValue={q} placeholder="Cerca un prodotto..."
              onKeyDown={(e) => e.key === "Enter" && setParam("q", e.target.value)}
              className="w-full bg-white border border-slate-200 rounded-full pl-9 pr-4 py-2 text-sm outline-none focus:ring-2 focus:ring-secondary" />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        {isLoading ? (
          <div className="text-center py-20 text-slate-400">{t("common.loading")}</div>
        ) : products.length === 0 ? (
          <div className="text-center py-24 max-w-lg mx-auto animate-float-up" data-testid="shop-empty-state">
            <div className="h-20 w-20 rounded-3xl bg-primary/8 text-primary flex items-center justify-center mx-auto mb-6">
              <ShoppingBag className="h-9 w-9" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight text-slate-900">Stiamo riempiendo gli scaffali.</h2>
            <p className="mt-3 text-lg font-accent italic text-secondary">Con calma. Le cose buone richiedono tempo.</p>
            <p className="mt-4 text-sm text-slate-500">Prodotti tipici, artigianato e souvenir del Gargano e delle Dolomiti arriveranno presto.</p>
            <Link to="/esperienze" className="mt-8 inline-flex items-center gap-2 rounded-full bg-primary text-white font-semibold px-7 py-3 hover:bg-primary/90 transition-colors">
              Intanto scopri le esperienze
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
            {products.map((p, i) => (
              <Link key={p.id} to={`/shop/${p.slug}`} data-testid={`product-card-${p.slug}`}
                className="group rounded-2xl bg-white border border-slate-100 shadow-sm hover-lift overflow-hidden animate-float-up"
                style={{ animationDelay: `${Math.min(i * 40, 400)}ms` }}>
                <div className="relative h-40 overflow-hidden">
                  <img src={p.images?.[0]} alt={p.name} className="h-full w-full object-cover img-zoom" loading="lazy" />
                  {p.stock === 0 && <span className="absolute top-2 left-2 rounded-full bg-slate-900/80 text-white text-[10px] font-semibold px-2 py-1">Esaurito</span>}
                </div>
                <div className="p-4">
                  <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{p.destination}</div>
                  <div className="font-heading font-semibold text-slate-900 text-sm mt-1 leading-tight line-clamp-2">{p.name}</div>
                  <div className="mt-2 flex items-center gap-2">
                    {p.discount_price ? (
                      <>
                        <span className="font-semibold text-primary">{eur(p.discount_price)}</span>
                        <span className="text-xs text-slate-400 line-through">{eur(p.price)}</span>
                      </>
                    ) : <span className="font-semibold text-primary">{eur(p.price)}</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
      <SponsorSlot placement="SHOP" className="pb-14" />
    </Layout>
  );
}
